import { Box, Container, Typography, Grid, Paper, keyframes } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HistoryIcon from '@mui/icons-material/History';
import AppsIcon from '@mui/icons-material/Apps';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

function ProblemSolutionSection() {
  const { t } = useTranslation();

  const problems = [
    {
      icon: <AccessTimeIcon sx={{ fontSize: { xs: 40, md: 48 } }} />,
      problem: t('landing.problem.timeConsuming.problem'),
      solution: t('landing.problem.timeConsuming.solution'),
      color: '#90caf9',
    },
    {
      icon: <HistoryIcon sx={{ fontSize: { xs: 40, md: 48 } }} />,
      problem: t('landing.problem.lostHistory.problem'),
      solution: t('landing.problem.lostHistory.solution'),
      color: '#f48fb1',
    },
    {
      icon: <AppsIcon sx={{ fontSize: { xs: 40, md: 48 } }} />,
      problem: t('landing.problem.scattered.problem'),
      solution: t('landing.problem.scattered.solution'),
      color: '#ffa726',
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: '#2c3034',
        position: 'relative',
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(144, 202, 249, 0.3), transparent)',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 3, sm: 3, md: 3 } }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 700,
            textAlign: 'center',
            mb: { xs: 4, md: 8 },
            color: '#FFFFFF',
          }}
        >
          {t('landing.problem.title')}
        </Typography>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          {problems.map((item, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(44, 48, 52, 0.8) 0%, rgba(33, 37, 41, 0.95) 100%)',
                  border: '1px solid rgba(144, 202, 249, 0.1)',
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: `${slideIn} 0.6s ease-out ${index * 0.1}s both`,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: `linear-gradient(90deg, ${item.color}, transparent)`,
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    border: `1px solid ${item.color}40`,
                    boxShadow: `0 20px 60px ${item.color}20`,
                    '&::before': {
                      opacity: 1,
                    },
                  },
                }}
              >
                {/* Icon with background glow */}
                <Box
                  sx={{
                    width: { xs: 64, md: 80 },
                    height: { xs: 64, md: 80 },
                    borderRadius: '20px',
                    background: `linear-gradient(135deg, ${item.color}15, ${item.color}05)`,
                    border: `1px solid ${item.color}30`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: { xs: 2, md: 3 },
                    color: item.color,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: -10,
                      background: `radial-gradient(circle, ${item.color}10, transparent 70%)`,
                      borderRadius: '50%',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '&:hover::after': {
                      opacity: 1,
                    },
                  }}
                >
                  {item.icon}
                </Box>

                {/* Problem */}
                <Typography
                  variant="body1"
                  sx={{
                    color: '#ADB5BD',
                    mb: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  {item.problem}
                </Typography>

                {/* Arrow */}
                <ArrowForwardIcon
                  sx={{
                    color: item.color,
                    fontSize: { xs: 20, md: 24 },
                    display: 'block',
                    mb: { xs: 1.5, md: 2 },
                    animation: `${keyframes`
                      0%, 100% { transform: translateX(0); }
                      50% { transform: translateX(5px); }
                    `} 2s ease-in-out infinite`,
                  }}
                />

                {/* Solution */}
                <Typography
                  variant="h6"
                  sx={{
                    color: '#DEE2E6',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    lineHeight: 1.5,
                  }}
                >
                  {item.solution}
                </Typography>

                {/* Decorative corner element */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -30,
                    right: -30,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${item.color}10, transparent)`,
                    opacity: 0.5,
                    pointerEvents: 'none',
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default ProblemSolutionSection;
